import {
  Component,
  inject,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxPermissionsService } from 'ngx-permissions';
import { ConfirmationService, MessageService, PrimeIcons } from 'primeng/api';
import { Dropdown } from 'primeng/dropdown';
import { DialogService } from 'primeng/dynamicdialog';
import { TabView, TabViewChangeEvent } from 'primeng/tabview';
import { lastValueFrom, map } from 'rxjs';
import {
  Address,
  AddressControllerService,
  CreditCard,
  CreditCardPaymentSummary,
  DropdownFilter,
  FinancialControllerService,
  InfluencerControllerService,
  InfluencerDetail,
  Orders,
  OrdersControllerService,
  PaymentControllerService,
  PersonControllerService,
  PersonDetail,
  ProductVariantDTO,
  Reward,
  RewardControllerService,
  ShippingCompanyControllerService,
  ShippingCompanyDTO,
  ShippingOrder,
  ShippingOrderAddressChange,
  ShippingOrderCompanyLogDetail,
  ShippingOrderControllerService,
  ShippingOrderItem,
  ShippingOrderItemDetail,
  ShippingOrderItemLogDetail,
  ShippingOrderRequest,
  ShippingOrderRequestItem,
  ShippingOrderStatus,
  SubscriberControllerService,
  SubscriberEditionCompositionSummary
} from 'src/app/admin-api';
import { ProductVariantSelectModalComponent } from 'src/app/components/product-select-modal/product-variant-select-modal/product-variant-select-modal.component';
import { ShippingTrackingModalComponent } from 'src/app/components/shipping-tracking-modal/shipping-tracking-modal.component';
import { TableActionButton, TableColumn } from 'src/app/components/table';
import { Role, roleAsObject, TrackingModel } from 'src/app/models';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { OpenService } from 'src/app/services/open.service';
import { GatewayUtil } from 'src/app/utils/gateway.util';
import { environment } from 'src/environments/environment';
import { ShippingOrderProductsRefundModalComponent } from '../shipping-order-products-refund-modal/shipping-order-products-refund-modal.component';
import { ShippingOrderRefundModalComponent } from '../shipping-order-refund-modal/shipping-order-refund-modal.component';
import { ShippingOrderItemUpdateModalComponent } from './shipping-order-item-update-modal/shipping-order-item-update-modal.component';

@Component({
  selector: 'app-shipping-order-detail',
  templateUrl: './shipping-order-detail.component.html',
  styleUrl: './shipping-order-detail.component.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [DialogService, MessageService, ConfirmationService]
})
export class ShippingOrderDetailComponent implements OnInit {
  @ViewChild('companies') dropdownCompanies: Dropdown | undefined;
  @ViewChild('address') dropdownAddress: Dropdown | undefined;
  @ViewChild(TabView) tabView: TabView | undefined;

  private shippingOrderService = inject(ShippingOrderControllerService);
  private activatedRoute = inject(ActivatedRoute);
  private personService = inject(PersonControllerService);
  private dialogService = inject(DialogService);
  private rewardService = inject(RewardControllerService);
  private ngxPersmissionService = inject(NgxPermissionsService);
  private messageService = inject(MessageService);
  private ordersService = inject(OrdersControllerService);
  private financialService = inject(FinancialControllerService);
  private paymentService = inject(PaymentControllerService);
  private openService = inject(OpenService);
  private subscriberService = inject(SubscriberControllerService);
  private influencerService = inject(InfluencerControllerService);
  private title = inject(Title);
  private shippingCompanyService = inject(ShippingCompanyControllerService);
  private addressService = inject(AddressControllerService);
  private router = inject(Router);
  private confirmationService = inject(ConfirmationService);

  shippingOrder: ShippingOrder;
  items: Array<ShippingOrderItemDetail | ShippingOrderRequestItem>;
  types: DropdownFilter[];
  person: PersonDetail;
  addressList: Address[];
  updateAddress = false;
  shippingAddress: Address[];
  influencer: InfluencerDetail;
  productCols = [
    new TableColumn.Builder()
      .setHeader('')
      .setField('defaultImageURL')
      .setSortable(false)
      .setFilter(false)
      .setType('image')
      .setStyleClass('product-image')
      .build(),
    new TableColumn.Builder()
      .setHeader('SKU Admin')
      .setField('productVariantId')
      .setType('number')
      .setRouterLink('/products/catalog/product-variant/')
      .setRouterLinkFieldName('productVariantId')
      .build(),
    new TableColumn.Builder()
      .setHeader('Produto')
      .setField('productVariantName')
      .setRouterLink('/products/catalog/product-variant/')
      .setRouterLinkFieldName('productVariantId')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Qtd.')
      .setField('quantity')
      .setType('number')
      .setCondition('gte')
      .build(),
    new TableColumn.Builder()
      .setHeader('Preço custo')
      .setField('costPrice')
      .setType('currency')
      .setCondition('gte')
      .build(),
    new TableColumn.Builder()
      .setHeader('Preço nota')
      .setField('invoicePrice')
      .setType('currency')
      .setCondition('gte')
      .build(),
    new TableColumn.Builder()
      .setHeader('Preço venda')
      .setField('salePrice')
      .setType('currency')
      .setCondition('gte')
      .build(),
    new TableColumn.Builder()
      .setHeader('Desconto')
      .setField('discount')
      .setType('currency')
      .setCondition('gte')
      .build()
  ];
  refundCols = [
    new TableColumn.Builder()
      .setHeader('Data estorno')
      .setFilter(false)
      .setField('dateCreated')
      .setType('date')
      .build(),
    new TableColumn.Builder()
      .setHeader('')
      .setField('defaultImage')
      .setSortable(false)
      .setFilter(false)
      .setType('image')
      .setStyleClass('product-image')
      .build(),
    new TableColumn.Builder()
      .setHeader('SKU Admin')
      .setField('productVariantId')
      .setType('number')
      .setFilter(false)
      .setRouterLink('/products/catalog/product-variant/')
      .setRouterLinkFieldName('productVariantId')
      .build(),
    new TableColumn.Builder()
      .setHeader('Produto')
      .setField('productVariantName')
      .setFilter(false)
      .setRouterLink('/products/catalog/product-variant/')
      .setRouterLinkFieldName('productVariantId')
      .build(),
    new TableColumn.Builder()
      .setHeader('Qtd.')
      .setField('quantity')
      .setType('number')
      .setFilter(false)
      .build(),
    new TableColumn.Builder()
      .setHeader('Glampoints.')
      .setField('points')
      .setType('number')
      .setFilter(false)
      .build()
  ];
  rewards: Reward[];
  refunds: ShippingOrderItemRefund[];
  permission = false;
  permissionSubstitution = false;
  orders: Orders[];
  editionOrder: Orders;
  payment: CreditCardPaymentSummary;
  creditCard: CreditCard;
  gatewayPayment: any;
  edition?: SubscriberEditionCompositionSummary;
  extraItemsCols = [
    new TableColumn.Builder()
      .setHeader('Id pedido')
      .setField('shippingOrderId')
      .setType('number')
      .setRouterLink('/products/shipping-orders/')
      .setRouterLinkFieldName('shippingOrderId')
      .build()
  ].concat(
    this.productCols.concat([
      new TableColumn.Builder()
        .setHeader('Tipo')
        .setField('shippingOrderTypeId')
        .setFilter(false)
        .setDisplayFunction((item: ShippingOrderItemDetail) =>
          this.shippingOrderTypeName(item.shippingOrderTypeId)
        )
        .build()
    ])
  );
  extraItems: ShippingOrderItemDetail[];
  referenceName: string;
  productSelectionModal = (item?: ShippingOrderRequestItem) => {
    const header = item
      ? `Substituir produto(s) ${item.productVariantName} (EAN: ${item.internalEAN})`
      : 'Adicionar produto';
    this.dialogService
      .open(ProductVariantSelectModalComponent, {
        header,
        dismissableMask: undefined,
        width: '60%',
        data: {
          type: 1,
          sorting: 'stock',
          search: item?.productVariantName.split(' ')?.at(0),
          buttonLabel: 'Avançar',
          sortingEnable: false,
          productsToAvoid: this.items?.map((i) => i.productVariantId),
          avoidReason: 'Já incluído'
        }
      })
      .onClose.subscribe(async (product: ProductVariantDTO) => {
        if (product && item) {
          this.dialogService
            .open(ShippingOrderItemUpdateModalComponent, {
              header: 'Substituição de item',
              data: {
                product,
                shippingOrder: this.shippingOrder,
                shippingOrderItem: item
              }
            })
            .onClose.subscribe(async (items: ShippingOrderItem[]) => {
              if (items?.length) {
                await this.findShippingOrderById(
                  this.shippingOrder.shippingOrderId
                );
                await Promise.all([
                  this.findResendItems(),
                  this.findShippingOrderItemsByShippingOrderId(),
                  this.findItemUpdateLogs()
                ]);
                this.messageService.add({
                  severity: 'success',
                  summary: 'Sucesso',
                  detail: 'Produto substituído com sucesso.'
                });
              } else if (items === null) this.productSelectionModal(item);
              LoaderService.showLoader(false);
            });
        } else if (product) {
          await this.addProduct(product);
        }
      });
  };

  confirmRemoveItem = (
    item: ShippingOrderItemDetail | ShippingOrderRequestItem
  ) => {
    if (this.permissionSubstitution && this.pendingResend)
      this.confirmationService.confirm({
        message: `Tem certeza que deseja remover o produto ${item.productVariantName}?`,
        acceptLabel: 'Remover produto',
        acceptButtonStyleClass: 'p-button-danger',
        rejectLabel: 'Manter produto',
        rejectButtonStyleClass: 'p-button-primary',
        accept: async () => {
          await this.deleteItem(item);
        }
      });
  };

  actionButtons: TableActionButton[] = [
    new TableActionButton.Builder()
      .setIcon(PrimeIcons.ARROW_RIGHT_ARROW_LEFT)
      .setAction(this.productSelectionModal)
      .setSize('small')
      .setText(true)
      .setRounded(true)
      .setTooltip('Substituir produto')
      .setTooltipPosition('bottom')
      .setDisplay((item: ShippingOrderRequestItem) => {
        return item.shippingOrderStatus === 15 && item.inStockAcquisition <= 0;
      })
      .build(),
    new TableActionButton.Builder()
      .setIcon(PrimeIcons.TRASH)
      .setAction(this.confirmRemoveItem)
      .setSize('small')
      .setText(true)
      .setRounded(true)
      .setStyleClass('p-button-danger')
      .setTooltip('Remover produto')
      .setTooltipPosition('bottom')
      .build()
  ];
  shippingCompany: ShippingCompanyDTO;
  shippingCompaniesAvailable: DropdownFilter[];
  updateCompany = false;
  companyServiceId: string | undefined;
  companyLogs: ShippingOrderCompanyLogDetail[];
  companyLogsCols = [
    new TableColumn.Builder()
      .setHeader('Usuário')
      .setField('userEmail')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Data')
      .setField('logDate')
      .setType('date')
      .setCondition('between')
      .build(),
    new TableColumn.Builder()
      .setHeader('Transportadora antiga')
      .setField('oldCompanyServiceName')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Transportadora nova')
      .setField('newCompanyServiceName')
      .setCondition('contains')
      .build()
  ];
  shippingOrderRequest: ShippingOrderRequest;
  statusList: ShippingOrderStatus[];
  addressChanges: ShippingOrderAddressChange[];
  colsAddressChanges = [
    new TableColumn.Builder()
      .setHeader('Data')
      .setField('dateCreated')
      .setCondition('between')
      .setType('date')
      .build(),
    new TableColumn.Builder()
      .setHeader('Endereço antigo')
      .setField('oldAddress')
      .setCondition('contains')
      .setDisplayFunction((address: ShippingOrderAddressChange) =>
        address.oldAddress?.concat(
          this.addressList?.find((a) => a.addressId === address.oldAddressId)
            ?.subscriber
            ? ' (Endereço da assinatura)'
            : ''
        )
      )
      .build(),
    new TableColumn.Builder()
      .setHeader('Novo endereço')
      .setField('address')
      .setCondition('contains')
      .setDisplayFunction((address: ShippingOrderAddressChange) =>
        address.address?.concat(
          this.addressList?.find((a) => a.addressId === address.addressId)
            ?.subscriber
            ? ' (Endereço da assinatura)'
            : ''
        )
      )
      .build()
  ];
  tabIndex = 0;
  itemLogs: ShippingOrderItemLogDetail[];
  itemLogCols: TableColumn[] = [
    new TableColumn.Builder()
      .setHeader('Data')
      .setField('dateCreated')
      .setType('date')
      .setCondition('between')
      .build(),
    new TableColumn.Builder()
      .setHeader('Produto antigo')
      .setField('oldProduct')
      .setCondition('contains')
      .setRouterLink('/products/catalog/product-variant/')
      .setRouterLinkFieldName('productVariantId')
      .build(),
    new TableColumn.Builder()
      .setHeader('Produto novo')
      .setField('newProduct')
      .setCondition('contains')
      .setRouterLink('/products/catalog/product-variant/')
      .setRouterLinkFieldName('productVariantId')
      .build(),
    new TableColumn.Builder()
      .setHeader('Usuário')
      .setField('username')
      .setCondition('contains')
      .build()
  ];

  ngOnInit(): void {
    this.ngxPersmissionService
      .hasPermission([
        roleAsObject(Role.Full_Administrator).enumValue,
        roleAsObject(Role.Financial).enumValue,
        roleAsObject(Role.Delivery).enumValue,
        roleAsObject(Role.Logistics).enumValue
      ])
      .then((permission) => (this.permission = permission))
      .catch(() => (this.permission = false));
    this.ngxPersmissionService
      .hasPermission([
        roleAsObject(Role.Full_Administrator).enumValue,
        roleAsObject(Role.Financial).enumValue,
        roleAsObject(Role.Delivery).enumValue,
        roleAsObject(Role.Logistics).enumValue,
        roleAsObject(Role.User_Allocation).enumValue,
        roleAsObject(Role.Admin_Allocation).enumValue
      ])
      .then((permission) => (this.permissionSubstitution = permission))
      .catch(() => (this.permissionSubstitution = false));
    this.activatedRoute.params.subscribe(async (params) => {
      const shippingOrderId = params['shippingOrderId'];
      if (shippingOrderId) {
        LoaderService.showLoader();
        await Promise.all([
          this.findShippingOrderById(Number(shippingOrderId)),
          this.findShippingOrderTypes(),
          this.findMillenniumOrder(Number(shippingOrderId)),
          this.findShippingOrderStatusList()
        ]);
        this.title.setTitle(
          `Detalhes do pedido de ${this.shippingOrderType} - ${shippingOrderId}`
        );
        await this.findBfluInfo();
        await Promise.all([
          this.findShippingOrderItemsByShippingOrderId(),
          this.findPersonInfo(),
          this.findRewards(),
          this.findPaymentInfo(),
          this.findSubscriberEdition(),
          this.findGiftReference(),
          this.findResendItems(),
          this.findShippingCompanyLogs(),
          this.findShippingOrderRequest(),
          this.findShippingCompany(),
          this.findShippingCompanyAndServiceList(),
          this.findAddressChanges(),
          this.findItemUpdateLogs()
        ]);
        this.activatedRoute.queryParams.subscribe((queryParams) => {
          if (queryParams['tab'] && !Number.isNaN(queryParams['tab'])) {
            this.tabIndex = Number(queryParams['tab']);
          }
        });
        LoaderService.showLoader(false);
      } else {
        AppDialogService.showErrorDialog(
          {
            error: { message: 'Shipping Order ID is required.' }
          },
          true
        );
      }
    });
  }

  async findShippingOrderById(shippingOrderId: number): Promise<void> {
    try {
      const order = await lastValueFrom(
        this.shippingOrderService
          .getShippingOrderById(shippingOrderId)
          .pipe(map((data) => data.result))
      );
      if (
        this.shippingOrder?.shippingOrderStatus !== undefined &&
        this.shippingOrder.shippingOrderStatus !== order.shippingOrderStatus
      )
        this.messageService.add({
          severity: 'info',
          summary: 'Atualização',
          detail: `Status do pedido atualizado para ${this.orderStatus(order)}`
        });
      this.order = order;
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
  }

  async findShippingOrderItemsByShippingOrderId(): Promise<void> {
    if (
      this.shippingOrder?.shippingOrderId &&
      (!this.isResend ||
        ![15, 16].includes(this.shippingOrder.shippingOrderStatus)) // AG.Estoque e Ag. Portfolio
    )
      try {
        if (this.productCols.some((c) => c.field === 'inStockAcquisition'))
          this.productCols = this.productCols.filter(
            (c) => c.field !== 'inStockAcquisition'
          );
        const items = await lastValueFrom(
          this.shippingOrderService
            .getShippingOrderItems(this.shippingOrder.shippingOrderId)
            .pipe(map((data) => data.result))
        );
        if (
          this.pendingResend &&
          this.permissionSubstitution &&
          !this.productCols.some((c) => c.type === 'button')
        )
          this.productCols.push(
            new TableColumn.Builder()
              .setHeader('Ação')
              .setField(' ')
              .setType('button')
              .build()
          );
        if (
          items.some(
            (i) => i.shippingOrderId !== this.shippingOrder.shippingOrderId
          )
        ) {
          this.items = items?.filter(
            (item) =>
              item.shippingOrderId === this.shippingOrder.shippingOrderId
          );
          this.extraItems = items.filter(
            (item) =>
              item.shippingOrderId !== this.shippingOrder.shippingOrderId
          );
        } else {
          this.items = [...items];
        }
      } catch (error) {
        AppDialogService.showErrorDialog(error);
      }
  }

  async findMillenniumOrder(shippingOrderId: number) {
    try {
      this.orders = await lastValueFrom(
        this.ordersService
          .getOrderByShippingOrderId(shippingOrderId)
          .pipe(map((data) => data.result))
      );
    } catch (error) {
      console.error('Error fetching Millennium order:', error);
    }
  }

  async findPaymentInfo() {
    if (this.shippingOrder?.creditCardPaymentId)
      try {
        this.payment = await lastValueFrom(
          this.paymentService
            .findCreditCardPaymentById(this.shippingOrder.creditCardPaymentId)
            .pipe(map((data) => data.result))
        );
        if (this.payment?.creditCardId)
          this.creditCard = await lastValueFrom(
            this.financialService
              .findCreditCardById(this.payment.creditCardId)
              .pipe(map((data) => data.result))
          );
        if (this.payment?.tid)
          this.gatewayPayment = await lastValueFrom(
            this.paymentService
              .getTransactionByCreditCardPaymentId(
                this.payment.creditCardPaymentId
              )
              .pipe(map((data) => data.result))
          );
      } catch (error) {
        console.error('Error fetching Millennium order:', error);
      }
  }

  async findShippingOrderTypes() {
    try {
      this.types = await lastValueFrom(
        this.shippingOrderService
          .getShippingOrderTypes()
          .pipe(map((data) => data.result))
      );
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
  }

  async findPersonInfo() {
    if (this.shippingOrder?.personId)
      try {
        this.person = await lastValueFrom(
          this.personService
            .findPersonInfoById(this.shippingOrder.personId)
            .pipe(map((data) => data.result))
        );
        if (this.pendingResend) {
          this.addressList = await lastValueFrom(
            this.addressService
              .getAddress(this.person.personId)
              .pipe(map((data) => data.result))
          );
          this.shippingAddress = this.addressList
            .filter((a) => a.addressType === 'SHIPPING')
            .reduce((list: Address[], a) => {
              if (
                list.every(
                  (ad: Address) =>
                    ad.addressId !== a.addressId &&
                    !(
                      ad.postalCode === a.postalCode &&
                      ad.streetNumber === a.streetNumber &&
                      ad.streetExtension === a.streetExtension
                    )
                )
              )
                list.push(a);
              else if (a.subscriber)
                list
                  .filter(
                    (ad) =>
                      ad.postalCode === a.postalCode &&
                      ad.streetNumber === a.streetNumber &&
                      ad.streetExtension === a.streetExtension
                  )
                  .forEach((ad) => (ad.subscriber = true));
              return list;
            }, []);
          if (
            !this.shippingOrder?.shippingInfoId ||
            this.shippingAddress.every(
              (a) => a.addressId !== this.shippingOrder?.shippingInfoId
            )
          )
            this.shippingOrder.shippingInfoId =
              this.shippingAddress.find(
                (a) =>
                  a.postalCode === this.shippingOrder.shippingPostalCode &&
                  a.streetNumber === this.shippingOrder.shippingStreetNumber &&
                  a.streetExtension ===
                    this.shippingOrder.shippingStreetExtensions
              )?.addressId ||
              this.shippingAddress.find(
                (a) =>
                  a.postalCode === this.shippingOrder.shippingPostalCode &&
                  a.streetNumber === this.shippingOrder.shippingStreetNumber
              )?.addressId ||
              this.shippingAddress.find(
                (a) => a.postalCode === this.shippingOrder.shippingPostalCode
              )?.addressId ||
              this.shippingOrder.shippingInfoId;
        }
      } catch (error) {
        AppDialogService.showErrorDialog(error);
      }
  }

  async findBfluInfo() {
    if (this.shippingOrder?.personId)
      try {
        this.influencer = await lastValueFrom(
          this.influencerService
            .findInfluencerDetails(this.shippingOrder.personId)
            .pipe(map((data) => data.result))
        );
      } catch (error) {
        this.influencer = null;
      }
  }

  rowBackground = (item: ShippingOrderRequestItem) => {
    if (
      this.isResend &&
      [15, 16].includes(item.shippingOrderStatus) && // Ag estoque e Ag. Portfolio
      (item as ShippingOrderRequestItem).inStockAcquisition <= 0
    ) {
      return '#ef444478';
    }
    return null;
  };

  shippingOrderTypeName(shippingOrderTypeId: number): string {
    return this.types?.find((t) => t.value === shippingOrderTypeId)?.label;
  }

  async findRewards() {
    if (this.shippingOrder?.shippingOrderTypeId === 3) {
      try {
        this.rewards = await lastValueFrom(
          this.rewardService
            .getRewardsByShippingOrderId(this.shippingOrder.shippingOrderId)
            .pipe(map((data) => data.result))
        );
        if (!this.rewards?.length && this.items?.length > 1)
          AppDialogService.showErrorDialog({
            error: {
              message:
                'Não foi possível identificar os glampoints trocados por produto.'
            }
          });
        this.refunds = this.rewards
          ?.filter((r) => r.rewardType === 6)
          ?.map((r) => this.item(r));
        const cols =
          this.shippingOrder.shippingOrderTypeId === 3
            ? this.productCols
            : this.extraItemsCols;
        if (!cols.some((c) => c.field === 'glampoints') && this.rewards?.length)
          cols.push(
            new TableColumn.Builder()
              .setHeader('Glampoints')
              .setField('glampoints')
              .setType('formattedNumber')
              .setSortable(false)
              .setFilter(false)
              .setDisplayFunction((item: ShippingOrderItemDetail) =>
                this.reward(item)
                  ? Math.abs(this.reward(item).points)
                  : undefined
              )
              .build()
          );
      } catch (error) {
        AppDialogService.showErrorDialog(error);
      }
    } else this.rewards = [];
  }

  refundProduct() {
    this.dialogService
      .open(ShippingOrderProductsRefundModalComponent, {
        header: `Estornar produto(s) de ${this.shippingOrderType} ${this.shippingOrder.shippingOrderId}`,
        data: {
          rewards: this.rewards,
          items: this.items,
          shippingOrder: this.shippingOrder
        },
        width: '600px'
      })
      .onClose.subscribe(async (success: boolean) => {
        if (success) {
          this.messageService.add({
            detail: 'Produto(s) estornado(s) com sucesso.',
            severity: 'success',
            summary: 'Sucesso'
          });
          LoaderService.showLoader();
          await this.findRewards();
          LoaderService.showLoader(false);
        }
      });
  }

  refundOrder() {
    this.dialogService
      .open(ShippingOrderRefundModalComponent, {
        header: `Estornar pedido de ${this.shippingOrderType} ${this.shippingOrder.shippingOrderId}`,
        data: {
          rewards: this.rewards,
          items: this.items,
          shippingOrder: this.shippingOrder,
          payment: this.payment,
          gatewayPayment: this.gatewayPayment
        },
        width: '600px'
      })
      .onClose.subscribe(async (order: ShippingOrder | false) => {
        if (order) {
          this.messageService.add({
            detail: 'Pedido estornado com sucesso.',
            severity: 'success',
            summary: 'Sucesso'
          });
          this.order = order;
          this.items = this.items.filter(
            (i) => i.shippingOrderId === order.shippingOrderId
          );
          LoaderService.showLoader();
          await Promise.all([
            this.findRewards(),
            this.findPaymentInfo(),
            this.findMillenniumOrder(this.shippingOrder.shippingOrderId)
          ]);
          LoaderService.showLoader(false);
        } else if (order === false) {
          this.refundProduct();
        }
      });
  }

  reward(item: ShippingOrderItemDetail) {
    return this.rewards?.find((r) => r.rewardId === item.rewardId);
  }

  item(reward: Reward): ShippingOrderItemDetail | ShippingOrderItemRefund {
    if (reward.rewardType === -1)
      return this.items?.find(
        (i) =>
          Object.prototype.hasOwnProperty.call(i, 'rewardId') &&
          (i as ShippingOrderItemDetail).rewardId === reward.rewardId
      );
    if (reward.rewardType === 6) {
      const item: ShippingOrderItemDetail = this.item({
        rewardType: -1,
        rewardId: reward.referenceId
      });
      return {
        dateCreated: reward.dateCreated,
        defaultImage: item.defaultImageURL,
        points: reward.points,
        productVariantId: item.productVariantId,
        productVariantName: item.productVariantName,
        quantity: item.quantity
      } as ShippingOrderItemRefund;
    }
    return null;
  }

  async findSubscriberEdition() {
    if (this.shippingOrder?.subscriberEditionId && !this.isResend) {
      try {
        this.edition = await lastValueFrom(
          this.subscriberService
            .findSubscriberEditionById(this.shippingOrder.subscriberEditionId)
            .pipe(map((data) => data.result))
        );
        if (this.edition?.shippingOrderId)
          this.editionOrder = await lastValueFrom(
            this.ordersService
              .getOrderByShippingOrderId(this.edition?.shippingOrderId)
              .pipe(map((data) => data.result?.at(0)))
          );
      } catch (error) {
        AppDialogService.showErrorDialog(error);
      }
    }
  }

  async findGiftReference() {
    if (this.shippingOrder?.shippingOrderTypeId === 4)
      try {
        this.referenceName = await lastValueFrom(
          this.shippingOrderService
            .getGiftReference(this.shippingOrder.shippingOrderId)
            .pipe(map((data) => data.result))
        );
      } catch (error) {
        AppDialogService.showErrorDialog(error);
      }
  }

  async openTrackingModal(): Promise<void> {
    LoaderService.showLoader();
    try {
      let tracking: TrackingModel[];
      const pedidoV =
        this.edition?.pedidoV || this.orders?.find((o) => o.pedidoV)?.pedidoV;
      const invoiceNumber =
        this.edition?.invoiceNumber || this.shippingOrder?.invoiceNumber;
      if (pedidoV)
        tracking = await this.openService.getShippingTrackingByPedidoV(pedidoV);
      if ((!pedidoV || !tracking?.length) && invoiceNumber)
        tracking = await this.openService.getShippingTracking(invoiceNumber);
      else {
        AppDialogService.showErrorDialog(
          {
            error: { message: 'Sem informações de rastreio para este pedido.' }
          },
          false
        );
        return;
      }

      if (tracking)
        this.dialogService.open(ShippingTrackingModalComponent, {
          showHeader: true,
          header: pedidoV
            ? `Rastreio do pedido ${pedidoV}`
            : `Rastreio da nota ${invoiceNumber}`,
          position: 'center',
          data: {
            shippingOrder: this.shippingOrder,
            tracking,
            edition: this.edition
          },
          dismissableMask: true,
          width: '50vw'
        });
      else
        AppDialogService.showErrorDialog({
          error: {
            message: 'Sem informações de rastreio para exibir.'
          }
        });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async findResendItems() {
    if (
      this.isResend &&
      [15, 16].includes(this.shippingOrder.shippingOrderStatus) // AG.Estoque e AG.Portfolio
    ) {
      try {
        if (!this.productCols.some((c) => c.field === 'inStockAcquisition'))
          this.productCols.push(
            new TableColumn.Builder()
              .setHeader('Estoque')
              .setField('inStockAcquisition')
              .setCondition('gte')
              .setType('formattedInteger')
              .build()
          );
        if (
          this.permissionSubstitution &&
          !this.productCols.some((c) => c.type === 'button')
        )
          this.productCols.push(
            new TableColumn.Builder()
              .setHeader('Ação')
              .setField(' ')
              .setType('button')
              .build()
          );
        this.items = await lastValueFrom(
          this.shippingOrderService
            .findShippingOrderRequestItems(this.shippingOrder.shippingOrderId)
            .pipe(map((data) => data.result))
        );
      } catch (error) {
        AppDialogService.showErrorDialog(error);
      }
    }
  }

  async approveOrder(): Promise<void> {
    if (
      this.shippingOrder?.shippingOrderId &&
      this.shippingOrder.shippingOrderStatus === 14 &&
      this.shippingOrderRequest?.status ===
        ShippingOrderRequest.StatusEnum.PENDENTE
    )
      try {
        LoaderService.showLoader();
        const order =
          (await lastValueFrom(
            this.shippingOrderService
              .approveResendOrders([
                this.shippingOrderRequest.shippingOrderRequestId
              ])
              .pipe(map((data) => data.result?.at(0)))
          )) || this.shippingOrder;
        if (order.shippingOrderStatus === 0) {
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: `Pedido de reenvio aprovado.`
          });
          this.order = order;
        } else if (order) {
          AppDialogService.showInfoDialog({
            message: `Infelizmente o estoque de alguns itens se esgotou enquanto o pedido aguardava aprovação, e o pedido voltou para "Ag.Estoque".`,
            header: 'Produtos esgotados',
            visible: true
          });
          this.order = order;
          await Promise.all([
            this.findShippingOrderItemsByShippingOrderId(),
            this.findResendItems()
          ]);
        }
      } catch (error) {
        AppDialogService.showErrorDialog(error);
      } finally {
        LoaderService.showLoader(false);
      }
  }

  async rejectOrder(): Promise<void> {
    if (this.pendingResend)
      try {
        LoaderService.showLoader();
        const order = await lastValueFrom(
          this.shippingOrderRequest?.shippingOrderRequestId
            ? this.shippingOrderService
                .rejectResendOrders([
                  this.shippingOrderRequest.shippingOrderRequestId
                ])
                .pipe(map((data) => data.result?.at(0)))
            : this.shippingOrderService
                .refundShippingOrder(this.shippingOrder?.shippingOrderId)
                .pipe(map((data) => data.result))
        );
        if (order) {
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: `Pedido de reenvio reprovado.`
          });
          this.order = order;
        }
      } catch (error) {
        AppDialogService.showErrorDialog(error);
      } finally {
        LoaderService.showLoader(false);
      }
  }

  async findShippingCompanyAndServiceList(): Promise<void> {
    if (this.canUpdateShippingCompany) {
      this.shippingCompaniesAvailable = await lastValueFrom(
        this.shippingOrderService.getAvailableShippingCompanies().pipe(
          map((data) => {
            data.result.sort((a, b) =>
              (a.shippingServiceName || a.shippingCompanyName).localeCompare(
                b.shippingServiceName || b.shippingCompanyName
              )
            );
            return data.result.map((s) => ({
              label: s.shippingServiceName || s.shippingCompanyName,
              value: s.id
            }));
          })
        )
      );
    }
  }

  async findShippingCompany() {
    try {
      if (this.companyServiceId)
        this.shippingCompany = await lastValueFrom(
          this.shippingCompanyService
            .getShippingCompanyById(this.companyServiceId)
            .pipe(map((data) => data.result))
        );
    } catch (error) {
      this.shippingCompany = null;
      console.error(error);
    }
  }

  async findShippingCompanyLogs() {
    if (this.shippingOrder?.shippingOrderId)
      try {
        this.companyLogs = await lastValueFrom(
          this.shippingOrderService
            .getShippingOrderCompanyLogDetails(
              this.shippingOrder.shippingOrderId
            )
            .pipe(map((data) => data.result))
        );
      } catch (error) {
        AppDialogService.showErrorDialog(error);
      }
  }

  async findShippingOrderRequest() {
    if (this.isResend)
      // Reenvio
      try {
        this.shippingOrderRequest = await lastValueFrom(
          this.shippingOrderService
            .findShippingOrderRequestFromResend(
              this.shippingOrder.shippingOrderId
            )
            .pipe(map((data) => data.result))
        );
      } catch (error) {
        this.shippingOrderRequest = null;
      }
  }

  updateShippingCompany() {
    this.updateCompany = true;
    setTimeout(() => {
      this.dropdownCompanies?.show();
    });
  }

  updateShippingAddress() {
    this.updateAddress = true;
    setTimeout(() => {
      this.dropdownAddress?.show();
    });
  }

  async saveShippingCompany() {
    if (this.companyServiceId) {
      LoaderService.showLoader();
      const [shippingCompanyId, shippingServiceId] =
        this.companyServiceId.split('_');
      try {
        const shippingOrder = await lastValueFrom(
          this.shippingOrderService
            .updateShippingCompany({
              shippingCompanyId: shippingCompanyId
                ? Number(shippingCompanyId)
                : null,
              shippingServiceId: shippingServiceId
                ? Number(shippingServiceId)
                : null,
              shippingOrderId: this.shippingOrder.shippingOrderId
            })
            .pipe(map((data) => data.result))
        );
        this.order = shippingOrder;
        await Promise.all([
          this.findShippingCompanyLogs(),
          this.findShippingCompany()
        ]);
      } catch (error) {
        AppDialogService.showErrorDialog(error);
      } finally {
        this.updateCompany = false;
        LoaderService.showLoader(false);
      }
    }
  }

  async findShippingOrderStatusList(): Promise<void> {
    this.statusList = await lastValueFrom(
      this.shippingOrderService
        .getShippingOrderStatusList()
        .pipe(map((data) => data.result))
    );
  }

  async saveAddress() {
    try {
      LoaderService.showLoader();
      this.shippingOrder = await lastValueFrom(
        this.shippingOrderService
          .updateShippingOrderAddress({
            shippingOrderId: this.shippingOrder.shippingOrderId,
            addressId: this.shippingOrder.shippingInfoId
          })
          .pipe(map((data) => data.result))
      );
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Endereço atualizado com sucesso'
      });
      await this.findAddressChanges();
      setTimeout(() => {
        this.navigate({
          index: this.tabView?.tabPanels?.length - 1 || 0,
          originalEvent: null
        });
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async findAddressChanges() {
    if (this.shippingOrder?.shippingOrderId)
      try {
        this.addressChanges = await lastValueFrom(
          this.shippingOrderService
            .getAddressChanges(this.shippingOrder.shippingOrderId)
            .pipe(map((data) => data.result))
        );
      } catch (error) {
        this.addressChanges = [];
        AppDialogService.showErrorDialog(error);
      }
  }

  navigate(event?: TabViewChangeEvent) {
    if (event) this.tabIndex = event.index;

    this.router.navigate(
      ['/products/shipping-orders/' + this.shippingOrder?.shippingOrderId],
      {
        queryParams: { tab: this.tabIndex },
        queryParamsHandling: 'merge'
      }
    );
  }

  async deleteItem(item: ShippingOrderItemDetail | ShippingOrderRequestItem) {
    if (this.permissionSubstitution && this.pendingResend)
      try {
        LoaderService.showLoader();
        const detail = await lastValueFrom(
          this.shippingOrderService
            .removeItemFromShippingOrder(
              this.shippingOrder.shippingOrderId,
              item.shippingOrderItemId
            )
            .pipe(map((data) => data.result))
        );
        this.items = this.items.filter(
          (i) => i.shippingOrderItemId !== item.shippingOrderItemId
        );
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail
        });
        await Promise.all([
          this.findShippingOrderById(this.shippingOrder.shippingOrderId),
          this.findItemUpdateLogs()
        ]);
      } catch (error) {
        AppDialogService.showErrorDialog(error);
      } finally {
        LoaderService.showLoader(false);
      }
  }

  async addProduct(product: ProductVariantDTO) {
    try {
      LoaderService.showLoader();
      const detail = await lastValueFrom(
        this.shippingOrderService
          .addItemToShippingOrder(
            product.productVariantId,
            this.shippingOrder.shippingOrderId
          )
          .pipe(map((data) => data.result))
      );
      this.items.push({ ...detail, inStockAcquisition: product.stock - 1 } as
        | ShippingOrderItemDetail
        | ShippingOrderRequestItem);
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Produto adicionado ao pedido'
      });
      await this.findItemUpdateLogs();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  orderStatus(order: ShippingOrder) {
    if (!order) return null;
    return this.statusList.find(
      (s) => s.shippingOrderStatusId === (order.shippingOrderStatus || 0)
    )?.shippingOrderStatusName;
  }

  async recalculateStocks(): Promise<void> {
    if (this.availableStock)
      try {
        LoaderService.showLoader();
        const result = await lastValueFrom(
          this.shippingOrderService
            .refreshShippingOrderRequestStocks(
              this.shippingOrder.shippingOrderId
            )
            .pipe(map((data) => data.result))
        );
        if (result?.length) {
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: `Pedido atualizado para 'Ag. Aprovação'.`
          });
          this.order = result.at(0);
        }
      } catch (error) {
        AppDialogService.showErrorDialog(error);
      } finally {
        LoaderService.showLoader(false);
      }
  }

  async findItemUpdateLogs() {
    if (this.shippingOrder?.shippingOrderId)
      try {
        this.itemLogs = await lastValueFrom(
          this.shippingOrderService
            .getShippingOrderItemLogs(this.shippingOrder.shippingOrderId)
            .pipe(map((data) => data.result))
        );
      } catch (error) {
        this.itemLogs = [];
        console.error(error);
      }
  }

  get shippingOrderType() {
    return this.shippingOrderTypeName(this.shippingOrder?.shippingOrderTypeId);
  }

  get shippingOrderStatus() {
    return this.orderStatus(this.shippingOrder);
  }

  get shippingOrderStatusClass() {
    switch (this.shippingOrder?.shippingOrderStatus) {
      case -2:
      case -1:
      case 10:
      case 11:
        return { 'status--danger': true };
      case 1:
      case 2:
      case 3:
      case 4:
      case 5:
      case 6:
      case 7:
      case 8:
      case 12:
      case 13:
      case 14:
      case 15:
      case 16:
        return { 'status--warning': true };
      case 9:
        return { 'status--success': true };
      default:
        return {};
    }
  }

  get order(): Orders {
    return (
      this.editionOrder ||
      this.orders?.find(
        (o) => o.statusOrderDescription?.toUpperCase() === 'ENTREGUE'
      ) ||
      this.orders?.find((o) =>
        [
          'AG. ESTOQUE',
          'AVERIGUAR FALHA NA ENTREGA',
          'CANCELADO',
          'CANCELADO - PEDIDO DOUGLAS',
          'DESPACHADO',
          'DEVOLVIDO',
          'EM DEVOLUÇÃO',
          'FALHA NA ENTREGA'
        ].includes(o.statusOrderDescription?.toUpperCase())
      ) ||
      this.orders?.find((o) =>
        [
          'AG RESSUPRIMENTO',
          'AG. RESSUPRIMENTO',
          'AG. APROVAÇÃO',
          'AG. CONFIRMAÇÃO DE ENTREGA',
          'AG. EMBARQUE',
          'AG. FATURAMENTO',
          'AG. LIBERAR EDIÇÕES',
          'EM TRANSITO'
        ].includes(o.statusOrderDescription?.toUpperCase())
      ) ||
      this.orders?.at(0) ||
      null
    );
  }

  get millenniumStatus() {
    return this.order
      ? this.order?.statusOrderDescription || 'Não informado'
      : 'Não exportado';
  }

  get millenniumStatusClass() {
    switch (this.millenniumStatus) {
      case 'AG. RESSUPRIMENTO':
      case 'AG. APROVAÇÃO':
      case 'AG. CONFIRMAÇÃO DE ENTREGA':
      case 'AG. EMBARQUE':
      case 'AG. FATURAMENTO':
      case 'AG. LIBERAR EDIÇÕES':
      case 'EM TRANSITO':
        return { 'status--warning': true };
      case 'AG. ESTOQUE':
      case 'AVERIGUAR FALHA NA ENTREGA':
      case 'CANCELADO':
      case 'CANCELADO - PEDIDO DOUGLAS':
      case 'DESPACHADO':
      case 'DEVOLVIDO':
      case 'EM DEVOLUÇÃO':
      case 'FALHA NA ENTREGA':
      case 'Não exportado':
        return { 'status--danger': true };
      case 'ENTREGUE':
        return { 'status--success': true };
      default:
        return {};
    }
  }

  get canRefundOrder() {
    return (
      this.shippingOrder &&
      this.shippingOrder.shippingOrderTypeId !== 1 &&
      [0, 4].includes(this.shippingOrder?.shippingOrderStatus) &&
      (!this.orders?.length ||
        this.orders?.every((o) =>
          [
            'AG. APROVAÇÃO',
            'AG. ASSINATURA',
            'AG. ESTOQUE',
            'AG RESSUPRIMENTO',
            'AG. LIBERAR EDIÇÕES',
            'AG. FATURAMENTO'
          ].includes(o.statusOrderDescription)
        ))
    );
  }

  get gatewayPaymentStatusClass() {
    switch (this.gatewayPayment?.status) {
      case 'paid':
      case 'captured':
        return { 'status--success': true };
      case 'pending':
        return { 'status--warning': true };
      case 'canceled':
      case 'failed':
      case 'not_authorized':
        return { 'status--warning': true };
      default:
        return {};
    }
  }

  get paymentStatusClass() {
    switch (this.payment?.transactionStatusId) {
      case 0:
      case 1:
      case 2:
      case 4:
      case 10:
        return { 'status--warning': true };
      case 6:
        return { 'status--success': true };
      case 3:
      case 5:
      case 9:
      case 12:
        return { 'status--danger': true };
      default:
        return {};
    }
  }

  get gatewayLink() {
    return environment.production ||
      [10, 12, 13, 17, 19, 21].includes(this.payment.gatewayId)
      ? GatewayUtil.gatewayUrl[this.payment.gatewayId]?.concat(this.payment.tid)
      : null;
  }

  get companyServiceName() {
    return this.companyServiceId
      ? this.shippingCompany?.shippingServiceName ||
          this.shippingCompany?.shippingCompanyName ||
          'Não identificada'
      : 'Sem transportadora';
  }

  get canUpdateShippingCompany() {
    return (
      this.permission &&
      this.isResend && // Reenvio
      (this.pendingResend ||
        (this.shippingOrder?.shippingOrderStatus === 0 &&
          !this.shippingOrder?.dateSyncERP) ||
        (this.editionOrder?.statusOrderDescription &&
          [
            'AG. APROVAÇÃO',
            'AG. ASSINATURA',
            'AG. ESTOQUE',
            'AG. RESSUPRIMENTO',
            'AG. LIBERAR EDIÇÕES',
            'AG. FATURAMENTO'
          ].includes(this.editionOrder.statusOrderDescription.toUpperCase())))
    );
  }

  get lastDateUpdatedMillennium() {
    return this.order?.dateUpdated || this.order?.dateCreated;
  }

  set order(shippingOrder: ShippingOrder) {
    this.shippingOrder = shippingOrder;
    this.companyServiceId =
      shippingOrder?.shippingServiceId || shippingOrder?.shippingCompanyId
        ? `${shippingOrder.shippingCompanyId || ''}_${shippingOrder.shippingServiceId || ''}`
        : null;
  }

  get isResend() {
    return this.shippingOrder?.shippingOrderTypeId === 2; // Reenvio
  }

  get pendingResend() {
    return (
      this.isResend &&
      [14, 15, 16].includes(this.shippingOrder.shippingOrderStatus)
    );
  }

  get availableStock() {
    return (
      this.isResend &&
      (this.permission || this.permissionSubstitution) &&
      this.shippingOrder?.shippingOrderStatus === 15 &&
      this.items?.every(
        (i: ShippingOrderRequestItem) => i.inStockAcquisition > 0
      )
    );
  }
}

export interface ShippingOrderItemRefund {
  defaultImage?: string;
  productVariantId?: number;
  productVariantName?: string;
  quantity?: number;
  dateCreated?: Date;
  points?: number;
}

export interface AddressAvailable extends Address {
  subscriber: boolean;
}
